import React, { useState, useMemo, useEffect } from "react";
import { TbSearch, TbArrowUp, TbArrowDown, TbChevronLeft, TbChevronRight, TbPlus } from "react-icons/tb";
import ManualHealthDataForm from "./ManualHealthDataForm";
import HealthDataConflictModal from "./HealthDataConflictModal";

const noopRegister = async () => ({});

const CompanyUserTable = ({
  tableData = {},
  userData = [],
  selectedUserId,
  onRegisterData = noopRegister,
  canRegisterData = true,
}) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [editingUserId, setEditingUserId] = useState(null);
  const [pendingConflict, setPendingConflict] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [formError, setFormError] = useState(null);
  const rowsPerPage = 10;

  useEffect(() => {
    setCurrentPage(1);
  }, [tableData, userData]);

  const handleRowClick = (e, row) => {
    if (e.target.tagName === "A") {
      e.preventDefault();
    }
    if (row.clickEvent) {
      row.clickEvent();
    }
  };

  const userMap = useMemo(() => {
    const map = new Map();
    for (const u of userData) {
      map.set(u.UsuarioID, u);
    }
    return map;
  }, [userData]);

  // Helper to extract raw values for sorting or searching
  const getRawValue = (row, field) => {
    if (field === "Nombre") {
      const user = userMap.get(row.UsuarioID);
      return user ? user.Nombre : "";
    }
    if (
      field === "StressAvgAVG"
      || field === "SleepHoursAVG"
      || field === "HRVValueAvgAVG"
      || field === "GlucoseAVG"
      || field === "BloodPressureAVG"
    ) {
      const user = userMap.get(row.UsuarioID);
      return user ? user[field] : row[field];
    }
    return row[field];
  };

  const getUserName = (row) => {
    const user = userMap.get(row.UsuarioID);
    if (user && user.Nombre) return user.Nombre;
    return typeof row.Nombre === "string" ? row.Nombre : `Colaborador ${row.UsuarioID}`;
  };

  const isDuplicateConflict = (error) => (
    error
    && error.status === 409
    && error.data
    && error.data.code === "CLOCK_DATA_DATE_EXISTS"
  );

  const getErrorMessage = (error) => (
    error && error.message ? error.message : "No se pudieron registrar los datos."
  );

  const openRegistration = (event, row) => {
    event.preventDefault();
    event.stopPropagation();
    setEditingUserId(row.UsuarioID);
    setPendingConflict(null);
    setFormError(null);
  };

  const closeRegistration = () => {
    setEditingUserId(null);
    setPendingConflict(null);
    setFormError(null);
  };

  const submitRegistration = async (row, formPayload) => {
    const payload = { usuarioId: row.UsuarioID, ...formPayload };
    setFormError(null);
    setIsSaving(true);
    try {
      await onRegisterData(payload);
      closeRegistration();
    } catch (error) {
      if (isDuplicateConflict(error)) {
        setPendingConflict(payload);
      } else {
        setFormError(getErrorMessage(error));
      }
    } finally {
      setIsSaving(false);
    }
  };

  const resolveConflict = async (resolution) => {
    if (!pendingConflict) return;
    setFormError(null);
    setIsResolving(true);
    try {
      await onRegisterData({ ...pendingConflict, resolution });
      closeRegistration();
    } catch (error) {
      if (isDuplicateConflict(error)) {
        setPendingConflict(pendingConflict);
      } else {
        setPendingConflict(null);
        setFormError(getErrorMessage(error));
      }
    } finally {
      setIsResolving(false);
    }
  };

  // Filter based on search term (name or ID)
  const filteredRows = useMemo(() => {
    if (!searchTerm.trim()) return tableData?.rows || [];
    const term = searchTerm.toLowerCase();
    return (tableData?.rows || []).filter((row) => {
      const name = getRawValue(row, "Nombre").toLowerCase();
      const id = String(row.UsuarioID);
      return name.includes(term) || id.includes(term);
    });
  }, [tableData?.rows, searchTerm, userMap]);

  // Sort rows
  const sortedRows = useMemo(() => {
    if (!sortField) return filteredRows;
    return [...filteredRows].sort((a, b) => {
      const valA = getRawValue(a, sortField);
      const valB = getRawValue(b, sortField);

      if (valA === undefined || valA === null) return 1;
      if (valB === undefined || valB === null) return -1;

      if (typeof valA === "number" && typeof valB === "number") {
        return sortDirection === "asc" ? valA - valB : valB - valA;
      }

      const strA = String(valA).toLowerCase();
      const strB = String(valB).toLowerCase();
      return sortDirection === "asc"
        ? strA.localeCompare(strB)
        : strB.localeCompare(strA);
    });
  }, [filteredRows, sortField, sortDirection, userMap]);

  // Pagination
  const totalPages = Math.ceil(sortedRows.length / rowsPerPage) || 1;
  const activePage = Math.min(currentPage, totalPages);
  const paginatedRows = useMemo(() => {
    const startIndex = (activePage - 1) * rowsPerPage;
    return sortedRows.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedRows, activePage]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="glass-filters-card p-6 w-full text-left space-y-4">
      {/* Header & Search */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-bold text-[#1D1F3F] font-display">Colaboradores</h3>
          <p className="text-xs text-slate-500 font-medium">Lista de usuarios y sus métricas generales</p>
        </div>
        <div className="relative w-full sm:w-72">
          <input
            type="text"
            placeholder="Buscar colaborador..."
            aria-label="Buscar colaborador"
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full h-10 pl-10 pr-4 bg-white/70 border border-slate-200 rounded-xl text-sm font-medium text-slate-700 placeholder-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#007FB4]/40 focus:bg-white transition-all"
          />
          <TbSearch className="absolute left-3.5 top-3 w-4 h-4 text-slate-400" />
        </div>
      </div>

      {/* Table Container */}
      <div className="overflow-x-auto -mx-6 px-6">
        <table className="w-full min-w-[800px] border-collapse">
          <thead>
            <tr className="border-b border-slate-200/60">
              {(tableData?.columns || []).map((col) => (
                <th
                  key={col.field}
                  onClick={() => handleSort(col.field)}
                  className="pb-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400 font-display cursor-pointer hover:text-[#1D1F3F] select-none transition-colors"
                  style={{ width: col.width }}
                  aria-sort={sortField === col.field ? (sortDirection === "asc" ? "ascending" : "descending") : "none"}
                >
                  <div className="flex items-center gap-1.5">
                    {col.label}
                    {sortField === col.field ? (
                      sortDirection === "asc" ? (
                        <TbArrowUp className="w-3.5 h-3.5 text-[#007FB4]" />
                      ) : (
                        <TbArrowDown className="w-3.5 h-3.5 text-[#007FB4]" />
                      )
                    ) : null}
                  </div>
                </th>
              ))}
              {canRegisterData && (
                <th className="pb-3 text-left text-xs font-bold uppercase tracking-wider text-slate-400 font-display">
                  Registro
                </th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {paginatedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={(tableData?.columns?.length || 1) + (canRegisterData ? 1 : 0)}
                  className="py-8 text-center text-sm text-slate-500 font-medium"
                >
                  No se encontraron colaboradores
                </td>
              </tr>
            ) : (
              paginatedRows.map((row) => {
                const isSelected = selectedUserId === row.UsuarioID;
                const userName = getUserName(row);
                return (
                  <React.Fragment key={row.UsuarioID}>
                    <tr
                      onClick={(e) => handleRowClick(e, row)}
                      className={`group cursor-pointer transition-colors hover:bg-white/40 ${
                        isSelected ? "bg-[#007FB4]/10 hover:bg-[#007FB4]/15" : ""
                      }`}
                    >
                      {(tableData?.columns || []).map((col) => (
                        <td key={col.field} className="py-3.5 text-sm font-medium text-slate-700">
                          {row[col.field]}
                        </td>
                      ))}
                      {canRegisterData && (
                        <td className="py-3.5 text-sm font-medium text-slate-700">
                          <div className="flex items-center justify-end opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
                            <button
                              type="button"
                              onClick={(event) => openRegistration(event, row)}
                              aria-label={`Registrar datos para ${userName}`}
                              className="inline-flex items-center gap-1 rounded-xl px-2.5 py-1.5 text-xs font-bold text-[#0075AC] transition-colors hover:bg-[#0075AC]/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#007FB4]/40"
                            >
                              <TbPlus className="h-4 w-4" />
                              <span>registrar datos</span>
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                    {editingUserId === row.UsuarioID && canRegisterData && (
                      <tr>
                        <td
                          colSpan={(tableData?.columns?.length || 1) + 1}
                          className="bg-slate-50/70 px-3 py-4"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <ManualHealthDataForm
                            userName={userName}
                            onSubmit={(payload) => submitRegistration(row, payload)}
                            onCancel={closeRegistration}
                            isSaving={isSaving || isResolving}
                            errorMessage={formError}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200/60 pt-4 text-sm font-semibold text-slate-600">
          <span>
            Página {activePage} de {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, activePage - 1))}
              disabled={activePage === 1}
              aria-label="Anterior"
              className="h-9 w-9 flex items-center justify-center bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-white rounded-xl text-slate-700 transition-colors"
            >
              <TbChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, activePage + 1))}
              disabled={activePage === totalPages}
              aria-label="Siguiente"
              className="h-9 w-9 flex items-center justify-center bg-white border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:hover:bg-white rounded-xl text-slate-700 transition-colors"
            >
              <TbChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      <HealthDataConflictModal
        isOpen={Boolean(pendingConflict)}
        onCancel={() => setPendingConflict(null)}
        onResolve={resolveConflict}
        isResolving={isResolving}
      />
    </div>
  );
};

export default CompanyUserTable;
